import React from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FormCaption, InformSpan, FormSubmit } from '../../common/components';
import { Input } from '../../common/components/form/input.component';
import {
  AUTH_PATTERN,
  PasswordErrors,
  AUTH_VALIDATION_CONFIG
} from './validation.pattern';
import { ROUTER_LINKS } from '../../common/consts/app-keys.const';
import { authService } from '../service/auth.service';
import { IResetPassword } from '../types';
import { APP_KEYS } from '../../common/consts';
import { useThrottle } from '../../hooks/useThrottle';

type Inputs = {
  password: string;
  confirmPassword: string;
};

export const CreateNewPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useParams<{ token: string }>();
  const email = (location.state as { email?: string })?.email;

  const {
    register,
    watch,
    handleSubmit,
    formState: { errors }
  } = useForm<Inputs>();

  const { mutate: resetPassword, isPending } = useMutation({
    mutationKey: [APP_KEYS.QUERY_KEYS.USER],
    mutationFn: (data: IResetPassword) => authService.resetPassword(data),
    onSuccess: () => {
      navigate(ROUTER_LINKS.PASSWORD_UPDATED);
    }
  });
  const throttledResetPassword = useThrottle(resetPassword);

  const onSubmit: SubmitHandler<Inputs> = (dataInputs) => {
    if (!email || !token) return;
    throttledResetPassword({
      email,
      password: dataInputs.password,
      token
    });
  };

  return (
    <>
      <FormCaption>{t('auth.newPasswordTitle')}</FormCaption>
      <InformSpan>
        {t('auth.newPasswordSubtitle')}
      </InformSpan>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col">
        <Input
          errors={errors}
          name="password"
          label={t('auth.newPasswordLabel')}
          placeholder={t('auth.enterHere')}
          type="password"
          register={register.bind(null, 'password', {
            required: { message: PasswordErrors.required, value: true },
            pattern: {
              value: AUTH_PATTERN.password,
              message: PasswordErrors.pattern
            },
            minLength: {
              value: AUTH_VALIDATION_CONFIG.password.minLength,
              message: PasswordErrors.minLength
            },
            maxLength: {
              value: AUTH_VALIDATION_CONFIG.password.maxLength,
              message: PasswordErrors.maxLength
            }
          })}
        />
        <Input
          errors={errors}
          name="confirmPassword"
          label={t('auth.confirmPasswordLabel')}
          placeholder={t('auth.enterHere')}
          type="password"
          register={register.bind(null, 'confirmPassword', {
            required: { message: PasswordErrors.required, value: true },
            validate: (val: string) => {
              if (watch('password') !== val) {
                return t('auth.passwordsNoMatch');
              }
            }
          })}
        />
        <FormSubmit
          className="md:mt-[2px] mb-[24px]"
          isLoading={isPending}
          value={t('auth.savePassword')}
        />
        <Link
          to={ROUTER_LINKS.SIGNIN}
          className="text-center text-base-black font-body text-[16px] font-medium hover:opacity-60"
        >
          {t('auth.backToLogin')}
        </Link>
      </form>
    </>
  );
};
